<template>
  <div class="card-modern">
  
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="card-title-modern">Monitoring SPH dan SPK</h3>
        <small class="text-muted">
          Daftar perusahaan (SPH) beserta status SPK.
        </small>
      </div>
    </div>
  
    <!-- FILTER + EXPORT -->
    <div class="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
      <button class="btn btn-sm bg-success-subtle border border-success-subtle text-success-emphasis"
        style="border-radius: 8px;" :disabled="exporting" @click="exportExcel">
        <span v-if="exporting">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else>
          <i class="bi bi-file-earmark-excel me-1"></i>Export Excel
        </span>
      </button>
  
      <div style="width: 200px;">
        <label class="form-label small text-muted mb-1">Cari Perusahaan</label>
        <input type="text" v-model="filters.search" class="form-control" style="border-radius: 8px;"
          @keyup.enter="fetchData(1)" />
      </div>
  
      <div style="width: 180px;">
        <label class="form-label small text-muted mb-1">Status SPK</label>
        <select v-model="filters.has_pks" class="form-select" style="border-radius: 8px;">
          <option value="">Semua</option>
          <option value="1">Ada SPK</option>
          <option value="0">Tidak Ada SPK</option>
        </select>
      </div>
  
      <button class="button-modern" @click="fetchData(1)">
        <i class="bi bi-search me-1"></i>Cari
      </button>
      <button class="btn btn-sm bg-light border text-dark" style="border-radius: 8px; height: 38px;" @click="resetFilter">
        Reset
      </button>
    </div>
  
    <!-- SUMMARY -->
    <div v-if="!loading" class="d-flex gap-3 mb-3 flex-wrap">
      <div class="summary-pill">
        Total: <strong>{{ pagination.total }}</strong>
      </div>
      <div class="summary-pill success">
        Ada SPK: <strong>{{ countHasSpk }}</strong>
      </div>
      <div class="summary-pill danger">
        Tidak Ada SPK: <strong>{{ countNoSpk }}</strong>
      </div>
    </div>
  
    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th>No</th>
            <th>Nama Perusahaan</th>
            <th class="text-center">SPK</th>
            <th>Alasan Tidak Ada SPK</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="4" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Memuat data...
            </td>
          </tr>
          <tr v-else-if="data.length === 0">
            <td colspan="4" class="text-center py-4 text-muted">
              Tidak ada data.
            </td>
          </tr>
          <tr v-for="(item, idx) in data" :key="item.id || idx" v-else>
            <td>{{ rowNumber(idx) }}</td>
            <td class="fw-bold">{{ item.mst_corporate_name || '-' }}</td>
            <td class="text-center">
              <span class="badge-modern" :class="Number(item.has_pks) === 1 ? 'badge-success' : 'badge-danger'">
                {{ Number(item.has_pks) === 1 ? 'Ada SPK' : 'Tidak Ada' }}
              </span>
            </td>
            <td>
              <template v-if="Number(item.has_pks) !== 1">
                <span v-if="item.pks_reason" class="text-muted">
                  <i class="bi bi-quote me-1"></i>{{ item.pks_reason }}
                </span>
                <span v-else class="text-muted fst-italic">Belum diisi</span>
              </template>
              <span v-else class="text-muted">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  
    <!-- PAGINATION -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <small class="text-muted">
        Menampilkan
        {{ data.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1 }}
        –
        {{ Math.min(pagination.page * pagination.limit, pagination.total) }}
        dari {{ pagination.total }} data
      </small>
      <div class="d-flex align-items-center gap-2">
        <small class="text-muted">Rows per page</small>
        <select v-model.number="pagination.limit" class="form-select form-select-sm" style="width: 70px;"
          @change="fetchData(1)">
          <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <nav>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: pagination.page === 1 }">
              <button class="page-link" @click="fetchData(pagination.page - 1)">‹</button>
            </li>
            <li v-for="(p, idx) in visiblePages" :key="idx" class="page-item"
              :class="{ active: p === pagination.page, disabled: p === '...' }">
              <button class="page-link" @click="p !== '...' && fetchData(p)">{{ p }}</button>
            </li>
            <li class="page-item" :class="{ disabled: pagination.page === totalPages }">
              <button class="page-link" @click="fetchData(pagination.page + 1)">›</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
// import { useRouter } from 'vue-router';
import reportSphSpkService from '@/services/reportSphSpkService';

// const router = useRouter();

const data = ref([]);
const loading = ref(false);
const exporting = ref(false);

const filters = reactive({
  search: '',
  has_pks: '',
});

const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0,
});

const perPageOptions = [10, 25, 50, 100];

const totalPages = computed(() =>
  Math.ceil(pagination.total / pagination.limit) || 1
);

const countHasSpk = computed(
  () => data.value.filter((d) => Number(d.has_pks) === 1).length
);
const countNoSpk = computed(
  () => data.value.filter((d) => Number(d.has_pks) !== 1).length
);

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = pagination.page;
  const delta = 1;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  pages.push(1);
  if (current - delta > 2) pages.push('...');
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    pages.push(i);
  }
  if (current + delta < total - 1) pages.push('...');
  pages.push(total);
  return pages;
});

const buildParams = (extra = {}) => {
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    ...extra,
  };
  if (filters.search) params.search = filters.search;
  if (filters.has_pks !== '') params.has_pks = filters.has_pks;
  return params;
};

const fetchData = async (page = 1) => {
  if (page < 1) return;
  loading.value = true;
  pagination.page = page;
  try {
    const res = await reportSphSpkService.getList(buildParams({ page }));
    data.value = res.data.data || [];
    pagination.page = res.data.pagination?.page || page;
    pagination.total = res.data.pagination?.total || 0;
  } catch (err) {
    console.error('Gagal load Monitoring SPH/SPK', err);
    alert('Gagal mengambil data Monitoring SPH dan SPK');
    data.value = [];
  } finally {
    loading.value = false;
  }
};

const resetFilter = () => {
  filters.search = '';
  filters.has_pks = '';
  fetchData(1);
};

const exportExcel = async () => {
  exporting.value = true;
  try {
    const res = await reportSphSpkService.exportExcel(buildParams({ page: 1, limit: 0 }));
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Monitoring_SPH_dan_SPK.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Export gagal', err);
    alert('Gagal export Excel');
  } finally {
    exporting.value = false;
  }
};

const rowNumber = (idx) => (pagination.page - 1) * pagination.limit + idx + 1;

// const formatDate = (date) => {
//   if (!date) return '-';
//   return new Intl.DateTimeFormat('id-ID', {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//   }).format(new Date(date));
// };

// const openDetail = (item) => {
//   if (!item?.id) return;
//   router.push(`/transport/transport-offers/detail/${item.id}`);
// };

onMounted(() => fetchData(1));
</script>

<style scoped>
.summary-pill {
  background: #f1f5f9;
  border-radius: 8px;
  padding: 0.4rem 0.85rem;
  font-size: 0.875rem;
  color: #334155;
}

.summary-pill.success {
  background: #dcfce7;
  color: #166534;
}

.summary-pill.danger {
  background: #fee2e2;
  color: #991b1b;
}
</style>